import { type ReactNode, memo } from 'react';
import { useParams } from 'react-router-dom';
import { Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { SplitScreenLayout } from '@/components/layout/SplitScreenLayout';
import { OnboardingMapIllustration } from '@/components/workspace/WorkspaceOnboardingPage/components/OnboardingMapIllustration';
import { OnboardingSettingsBar } from '@/components/layout/OnboardingSettingsBar';
import { LoadingState } from '@/components/common/loading';
import { FONT_WEIGHT_SEMIBOLD } from '@/constants/ui';
import { useDeviceLayout, useInvitationFlow } from '@/hooks';
import { InvitationLeftPanel } from './components/InvitationLeftPanel';
import { InvitationDetailsStep } from './components/InvitationDetailsStep';
import { InvitationSuccessContent } from './components/InvitationSuccessContent';
import { InvitationErrorState } from './components/InvitationErrorState';
import { InvitationMobileView } from './components/InvitationMobileView';
import { ProfileStep } from './components/ProfileStep';
import styles from './InvitationPage.module.css';

const DECLINED_ICON_SIZE = 48;

export const InvitationPage = memo(function InvitationPage(): ReactNode {
  const { token = '' } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();
  const flow = useInvitationFlow(token);

  if (isMobile) {
    return <InvitationMobileView flow={flow} />;
  }

  if (flow.step === 'success' && flow.acceptResult) {
    return <InvitationSuccessContent acceptResult={flow.acceptResult} />;
  }

  const renderRightPanel = (): ReactNode => {
    switch (flow.step) {
      case 'loading':
        return <LoadingState centered text={t('invitation.loading')} />;

      case 'details':
        if (!flow.invitation) return null;
        return (
          <InvitationDetailsStep
            invitation={flow.invitation}
            isAccepting={flow.isAccepting}
            isDeclining={flow.isDeclining}
            onAccept={flow.handleAccept}
            onDecline={flow.handleDecline}
          />
        );

      case 'profile':
        return <ProfileStep email={flow.invitation?.email ?? ''} />;

      case 'declined':
        return (
          <div className={styles.declinedWrapper}>
            <div className={styles.declinedIcon}>
              <IconX size={DECLINED_ICON_SIZE} />
            </div>
            <Text size="lg" fw={FONT_WEIGHT_SEMIBOLD}>
              {t('invitation.declined.title')}
            </Text>
            <Text c="dimmed" size="sm">
              {t('invitation.declined.description')}
            </Text>
          </div>
        );

      case 'error':
        if (!flow.errorType) return null;
        return (
          <InvitationErrorState
            errorType={flow.errorType}
            onRetry={flow.handleRetry}
            onLogout={flow.handleLogout}
          />
        );

      default:
        return null;
    }
  };

  return (
    <SplitScreenLayout
      leftPanelBackground={<OnboardingMapIllustration />}
      leftPanel={
        <InvitationLeftPanel step={flow.step} invitation={flow.invitation} />
      }
      rightPanel={renderRightPanel()}
      settingsBar={<OnboardingSettingsBar />}
    />
  );
});
